import { Formik, Form, Field, FieldArray } from 'formik';
import { FormikInput, FormikSelect, PrimaryButton, SecondaryButton } from "../UI";
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { releaseSchema } from '../../schemas';
import { createRelease, updateRelease } from '../../utils/APIs';
import { getLocalDateTimeForInput } from '../../utils/converter';
import InlineTooltip from '../UI/Tooltip/InlineTooltip';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const ReleaseModal = ({ setSelectedAction, selectedRelease, getRelease }) => {
    const [submitError, setSubmitError] = useState('');
    const { userData } = useUserContext();
    const isEdit = Boolean(selectedRelease);
    const { content } = useContentfulContext();

    const initialValues = {
        version: selectedRelease?.version || '',
        release_date: selectedRelease?.release_date ? getLocalDateTimeForInput(new Date(selectedRelease.release_date)) : getLocalDateTimeForInput(),
        status: selectedRelease?.status || 'draft',
        notification_configuration: {
            pre_release: selectedRelease?.notification_configuration?.pre_release?.map(item => ({
                title: item.title || '',
                description: item.description || '',
                schedule_time_before_release: item.schedule_time_before_release || '',
                is_email_required: item.is_email_required || false,
                email_recipients: item.email_recipients || [],
            })) || [],
            post_release: selectedRelease?.notification_configuration?.post_release?.map(item => ({
                title: item.title || '',
                description: item.description || '',
                schedule_time_after_release: item.schedule_time_after_release || '',
                is_email_required: item.is_email_required || false,
                email_recipients: item.email_recipients || [],
            })) || [],
        }
    }

    const addRelease = async (payload, actions) => {
        try {
            const result = await createRelease(userData.token, payload, userData?.selectedOrganization?.org_token);
            if (result.status) {
                toast.success(result.message);
                getRelease();
                actions.resetForm();
                setSubmitError('');
                setSelectedAction('');
            }
        } catch (error) {
            setSubmitError('Failed to create the release. Please try again.');
            toast.error(submitError);
            console.error('Create release error:', error);
            actions.setSubmitting(false);
        }
    };

    const editRelease = async (payload, actions) => {
        try {
            const result = await updateRelease(userData.token, selectedRelease._id, payload, userData?.selectedOrganization?.org_token);
            if (result.status) {
                toast.success(result.message);
                getRelease();
                actions.resetForm();
                setSubmitError('');
                setSelectedAction('');
            }
        } catch (error) {
            setSubmitError('Failed to update the release. Please try again.');
            toast.error(submitError);
            console.error('Edit release error:', error);
            actions.setSubmitting(false);
        }
    };

    const handleSubmit = async (values, actions) => {
        const payload = {
            ...values,
            release_date: new Date(values.release_date).toISOString(),
            notification_configuration: {
                pre_release: values.notification_configuration.pre_release.map(item => ({
                    ...item,
                    email_recipients: item.email_recipients.filter(email => email.trim() !== '')
                })),
                post_release: values.notification_configuration.post_release.map(item => ({
                    ...item,
                    email_recipients: item.email_recipients.filter(email => email.trim() !== '')
                }))
            }
        };

        if (isEdit) {
            editRelease(payload, actions);
        } else {
            addRelease(payload, actions);
        }
    };

    return (
        <PrimaryModal
            title={isEdit ? content.ADMIN_RELEASE.edit_release : content.ADMIN_RELEASE.create_release}
            hideModal={() => setSelectedAction('')}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={releaseSchema}
                onSubmit={handleSubmit}
                validateOnBlur
                validateOnChange
            >
                {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                    <Form className="space-y-4 p-6">
                        {/* Version Field */}
                        <FormikInput
                            id="version"
                            name="version"
                            label={content.ADMIN_RELEASE.form.version}
                            placeholder="e.g. 1.2.0"
                            helperText={content.ADMIN_RELEASE.form.help_txt.version}
                            mandatory
                        />

                        {/* Release Date Field */}
                        <FormikInput
                            type="datetime-local"
                            id="release_date"
                            name="release_date"
                            label={content.ADMIN_RELEASE.form.release_date}
                            helperText={content.ADMIN_RELEASE.form.help_txt.release_date}
                            mandatory
                        />

                        {/* Status Field */}
                        <FormikSelect
                            id="status"
                            name="status"
                            label={content.ADMIN_RELEASE.form.status}
                            options={[
                                { value: "draft", label: content.ADMIN_RELEASE.options.draft },
                                { value: "success", label: content.ADMIN_RELEASE.options.success },
                                { value: "failed", label: content.ADMIN_RELEASE.options.failed },
                            ]}
                            mandatory
                        />

                        {/* Pre Release Notifications */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{content.ADMIN_RELEASE.pre.heading}</h3>
                            <FieldArray name="notification_configuration.pre_release">
                                {({ insert, remove, push }) => (
                                    <div className="space-y-4">
                                        {values.notification_configuration.pre_release.map((_, index) => (
                                            <div key={index} className="border rounded-md p-4 bg-gray-50">
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <div className="flex gap-2">
                                                            <label htmlFor={`notification_configuration.pre_release.${index}.title`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.title}</label>
                                                            <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.title} imgClass={"pt-0.5"} />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name={`notification_configuration.pre_release.${index}.title`}
                                                            className="block w-full px-3 py-2 border rounded-md"
                                                        />
                                                        {errors.notification_configuration?.pre_release?.[index]?.title && touched.notification_configuration?.pre_release?.[index]?.title && (
                                                            <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.pre_release[index].title}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <div className="flex gap-2">
                                                            <label htmlFor={`notification_configuration.pre_release.${index}.schedule_time_before_release`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.schedule_time_before_release}</label>
                                                            <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.schedule_time_before_release} imgClass={"pt-0.5"} />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name={`notification_configuration.pre_release.${index}.schedule_time_before_release`}
                                                            className="block w-full px-3 py-2 border rounded-md"
                                                        />
                                                        {errors.notification_configuration?.pre_release?.[index]?.schedule_time_before_release && touched.notification_configuration?.pre_release?.[index]?.schedule_time_before_release && (
                                                            <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.pre_release[index].schedule_time_before_release}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mb-4">
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.pre_release.${index}.description`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.description}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.description} imgClass={"pt-0.5"} />
                                                    </div>
                                                    <Field
                                                        as="textarea"
                                                        name={`notification_configuration.pre_release.${index}.description`}
                                                        className="block w-full px-3 py-2 border rounded-md"
                                                        rows={3}
                                                    />
                                                    {errors.notification_configuration?.pre_release?.[index]?.description && touched.notification_configuration?.pre_release?.[index]?.description && (
                                                        <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.pre_release[index].description}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center mb-4">
                                                    <Field
                                                        type="checkbox"
                                                        name={`notification_configuration.pre_release.${index}.is_email_required`}
                                                        className="h-4 w-4 text-primary border-gray-300 rounded"
                                                    />
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.pre_release.${index}.is_email_required`} className="ml-2 block text-sm font-medium">{content.ADMIN_RELEASE.form.send_email}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.send_email} imgClass={"pt-0.5"} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.pre_release.${index}.email_recipients`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.email_recipients}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.email_recipients} imgClass={"pt-0.5"} />
                                                    </div>
                                                    <Field
                                                        type="text"
                                                        name={`notification_configuration.pre_release.${index}.email_recipients`}
                                                        className="block w-full px-3 py-2 border rounded-md"
                                                        placeholder="example1@domain.com, example2@domain.com"
                                                        onChange={(e) => {
                                                            const emails = e.target.value.split(',').map(email => email.trim());
                                                            setFieldValue(`notification_configuration.pre_release.${index}.email_recipients`, emails);
                                                        }}
                                                    />
                                                    {errors.notification_configuration?.pre_release?.[index]?.email_recipients && touched.notification_configuration?.pre_release?.[index]?.email_recipients && (
                                                        <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.pre_release[index].email_recipients}</p>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="mt-4 text-red-600 hover:text-red-800"
                                                    onClick={() => remove(index)}
                                                >
                                                    {content.ADMIN_RELEASE.pre.delete}
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="text-primary hover:text-primary-2"
                                            onClick={() => push({ title: '', description: '', schedule_time_before_release: '', is_email_required: false, email_recipients: [] })}
                                        >
                                            {content.ADMIN_RELEASE.pre.add}
                                        </button>
                                    </div>
                                )}
                            </FieldArray>
                        </div>

                        {/* Post Release Notifications */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{content.ADMIN_RELEASE.post.heading}</h3>
                            <FieldArray name="notification_configuration.post_release">
                                {({ insert, remove, push }) => (
                                    <div className="space-y-4">
                                        {values.notification_configuration.post_release.map((_, index) => (
                                            <div key={index} className="border rounded-md p-4 bg-gray-50">
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <div className="flex gap-2">
                                                            <label htmlFor={`notification_configuration.post_release.${index}.title`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.title}</label>
                                                            <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.title} imgClass={"pt-0.5"} />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name={`notification_configuration.post_release.${index}.title`}
                                                            className="block w-full px-3 py-2 border rounded-md"
                                                        />
                                                        {errors.notification_configuration?.post_release?.[index]?.title && touched.notification_configuration?.post_release?.[index]?.title && (
                                                            <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.post_release[index].title}</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="flex gap-2">
                                                            <label htmlFor={`notification_configuration.post_release.${index}.schedule_time_after_release`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.schedule_time_after_release}</label>
                                                            <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.schedule_time_after_release} imgClass={"pt-0.5"} />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name={`notification_configuration.post_release.${index}.schedule_time_after_release`}
                                                            className="block w-full px-3 py-2 border rounded-md"
                                                        />
                                                        {errors.notification_configuration?.post_release?.[index]?.schedule_time_after_release && touched.notification_configuration?.post_release?.[index]?.schedule_time_after_release && (
                                                            <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.post_release[index].schedule_time_after_release}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mb-4">
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.post_release.${index}.description`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.description}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.description} imgClass={"pt-0.5"} />
                                                    </div>
                                                    <Field
                                                        as="textarea"
                                                        name={`notification_configuration.post_release.${index}.description`}
                                                        className="block w-full px-3 py-2 border rounded-md"
                                                        rows={3}
                                                    />
                                                    {errors.notification_configuration?.post_release?.[index]?.description && touched.notification_configuration?.post_release?.[index]?.description && (
                                                        <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.post_release[index].description}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center mb-4">
                                                    <Field
                                                        type="checkbox"
                                                        name={`notification_configuration.post_release.${index}.is_email_required`}
                                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                                                    />
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.post_release.${index}.is_email_required`} className="ml-2 block text-sm font-medium">{content.ADMIN_RELEASE.form.send_email}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.send_email} imgClass={"pt-0.5"} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex gap-2">
                                                        <label htmlFor={`notification_configuration.post_release.${index}.email_recipients`} className="block text-sm font-medium mb-1">{content.ADMIN_RELEASE.form.email_recipients}</label>
                                                        <InlineTooltip content={content.ADMIN_RELEASE.form.help_txt.email_recipients} imgClass={"pt-0.5"} />
                                                    </div>
                                                    <Field
                                                        type="text"
                                                        name={`notification_configuration.post_release.${index}.email_recipients`}
                                                        className="block w-full px-3 py-2 border rounded-md"
                                                        placeholder="example1@domain.com, example2@domain.com"
                                                        onChange={(e) => {
                                                            const emails = e.target.value.split(',').map(email => email.trim());
                                                            setFieldValue(`notification_configuration.post_release.${index}.email_recipients`, emails);
                                                        }}
                                                    />
                                                    {errors.notification_configuration?.post_release?.[index]?.email_recipients && touched.notification_configuration?.post_release?.[index]?.email_recipients && (
                                                        <p className="mt-2 text-sm text-red-500">{errors.notification_configuration.post_release[index].email_recipients}</p>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="mt-4 text-red-600 hover:text-red-800"
                                                    onClick={() => remove(index)}
                                                >
                                                    {content.ADMIN_RELEASE.post.delete}
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="text-primary hover:text-primary-2"
                                            onClick={() => push({ title: '', description: '', schedule_time_after_release: '', is_email_required: false, email_recipients: [] })}
                                        >
                                            {content.ADMIN_RELEASE.post.add}
                                        </button>
                                    </div>
                                )}
                            </FieldArray>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end space-x-4">
                            <SecondaryButton type="button" handleClick={() => setSelectedAction('')}>
                                {content.ADMIN_RELEASE.cancel}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : isEdit ? content.ADMIN_RELEASE.edit_release : content.ADMIN_RELEASE.create_release}
                            </PrimaryButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </PrimaryModal>
    );
};

export default ReleaseModal;
